<template>
    <div v-if="invitationManagement.loading.value && !invitationManagement.management.value" class="flex min-h-64 items-center justify-center" :aria-busy="true">
        <LoadingSpinner class="size-6 text-ink-muted motion-reduce:animate-none" />
    </div>
    <section v-else class="flex flex-col gap-4">
        <p role="status" class="sr-only">{{ announcement }}</p>
        <!-- Takes focus when a member update removes the row's "…" (a removal or lead handoff), so focus has somewhere to return. -->
        <div
            ref="tableRegion"
            tabindex="-1"
            class="overflow-x-auto rounded-lg border border-border bg-surface-raised focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-control-border-strong">
            <table class="team-members" :aria-label="t('teamPerspective.members')">
                <thead>
                    <tr>
                        <th scope="col">{{ t("teamPage.name") }}</th>
                        <th scope="col">{{ t("teamPage.role") }}</th>
                        <th scope="col">{{ t("teamPage.perspective") }}</th>
                        <th v-if="hasEndColumn" scope="col" :class="endColumnWidth">
                            <span class="sr-only">{{ t("teamPage.actions") }}</span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="member in report.members"
                        :key="member.id"
                        class="group relative"
                        :class="{ 'hover:bg-surface focus-within:bg-surface': member.member_url || member.profile_url }">
                        <td>
                            <div class="flex items-center gap-3">
                                <PerspectiveAvatar :name="member.name" :letters="member.result?.letters" size="xs" aria-hidden="true" />
                                <div class="min-w-0">
                                    <BaseLink
                                        v-if="member.member_url || member.profile_url"
                                        :href="member.member_url || member.profile_url"
                                        class="team-member-link body-serif text-ink"
                                        :aria-label="t('teamPage.viewMember', { name: displayName(member) })"
                                        >{{ displayName(member) }}</BaseLink
                                    >
                                    <span v-else class="body-serif text-ink">{{ displayName(member) }}</span>
                                </div>
                            </div>
                            <i
                                v-if="member.member_url || member.profile_url"
                                class="bi bi-chevron-right pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-ink-muted opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
                                aria-hidden="true" />
                        </td>
                        <td class="body-small text-ink break-words">
                            <template v-if="member.job_title">{{ member.job_title }}</template>
                            <template v-else
                                ><span class="text-ink-muted" aria-hidden="true">{{ t("peopleDirectory.emptyCell") }}</span
                                ><span class="sr-only">{{ t("peopleDirectory.noRole") }}</span></template
                            >
                        </td>
                        <td class="body-small text-ink" :class="{ 'pr-10': !hasEndColumn }">
                            <!-- Raised above the row link's overlay so a truncated label can still be revealed by hover, focus or tap. -->
                            <BaseTooltip
                                v-if="member.result?.archetype"
                                :as-child="false"
                                open-on-click
                                class="relative z-20 flex max-w-full min-w-0 items-center gap-2 text-left"
                                content-class="fs-mask">
                                <template #trigger>
                                    <span
                                        v-if="getPerspectiveSwatch(member.result?.letters)"
                                        class="size-2 shrink-0 rounded-full"
                                        :style="{ backgroundColor: getPerspectiveSwatch(member.result?.letters)?.fill }"
                                        aria-hidden="true" />
                                    <span class="truncate">{{ member.result.archetype }}</span>
                                </template>
                                <template #content>{{ member.result.archetype }}</template>
                            </BaseTooltip>
                            <template v-else
                                ><span class="text-ink-muted" aria-hidden="true">{{ t("peopleDirectory.emptyCell") }}</span
                                ><span class="sr-only">{{ t("teamPage.noSharedResult") }}</span></template
                            >
                        </td>
                        <td v-if="hasEndColumn" class="team-member-actions">
                            <div class="flex items-center justify-end gap-2">
                                <BaseChip v-if="hasRoleChip(member)" size="2xs">{{ member.role === "lead" ? t("teamPage.lead") : t("teamPage.admin") }}</BaseChip>
                                <!-- Only the menu is raised above the row link's overlay, so the chip and the rest of the cell still open the member. -->
                                <div v-if="hasMenu(member)" class="relative z-20">
                                    <TeamMemberActions :source="source" :member="member" :team-name="report.name" :navigation="navigation" @updated="membershipUpdated" />
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <TeamPendingInvitations v-if="isLead" :key="report.id" :source="source" :team-id="report.id" :invitation-management="invitationManagement" />
    </section>
</template>
<script setup lang="ts">
import type { TeamPageNavigation, TeamPerspectiveReport } from "~vue/api/generated/team-perspective";
import BaseChip from "~vue/components/BaseChip.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import PerspectiveAvatar from "~vue/components/PerspectiveAvatar.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import TeamMemberActions from "~vue/Perspective/TeamMemberActions.vue";
import TeamPendingInvitations from "~vue/Perspective/TeamPendingInvitations.vue";
import type { TeamInvitationManagementState } from "~vue/Perspective/useTeamInvitationManagement";
import BaseLink from "~vue/router/BaseLink.vue";
import { TEAM_INTERACTION_SOURCE, type TeamInteractionSource } from "~vue/Teams/teamInteraction";
import { getPerspectiveSwatch } from "~vue/utils/perspectivePalette";
import { computed, nextTick, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
const {
    source = TEAM_INTERACTION_SOURCE.TEAM_PAGE,
    report,
    navigation,
    invitationManagement,
} = defineProps<{
    source?: TeamInteractionSource;
    report: TeamPerspectiveReport;
    navigation: TeamPageNavigation;
    invitationManagement: TeamInvitationManagementState;
}>();
const isLead = computed(() => !!report.add_members_url && report.members.some((member) => member.id === report.viewer_id && member.role === "lead"));
const emit = defineEmits<{ updated: [report: TeamPerspectiveReport] }>();
const { t } = useI18n();
const announcement = ref("");
const tableRegion = useTemplateRef<HTMLDivElement>("tableRegion");
const hasRoleChip = (member: TeamPerspectiveReport["members"][number]) => member.role === "lead" || member.role === "admin";
const hasMenu = (member: TeamPerspectiveReport["members"][number]) => Boolean(member.remove_url || member.make_lead_url);
// Members who can't manage the team still get this column, because it is where the lead/admin chip sits.
const hasEndColumn = computed(() => report.members.some((member) => hasRoleChip(member) || hasMenu(member)));
// Each width fits the widest row's chip and/or "…" plus the hover chevron's right padding.
const endColumnWidth = computed(() => {
    if (report.members.some((member) => hasRoleChip(member) && hasMenu(member))) {
        return "w-40";
    }
    return report.members.some(hasRoleChip) ? "w-32" : "w-18";
});
async function membershipUpdated(updated: TeamPerspectiveReport, message: string, memberId: string) {
    announcement.value = message;
    emit("updated", updated);
    // The dialog returns focus to that row's "…", which is gone if the member was removed or, after a lead handoff,
    // the viewer can no longer act on them; the table is the nearest surviving place to land.
    const triggerSurvives = updated.members.some((member) => member.id === memberId && (member.remove_url || member.make_lead_url));
    if (!triggerSurvives) {
        await nextTick();
        tableRegion.value?.focus();
    }
}
const displayName = (member: TeamPerspectiveReport["members"][number]) => (member.id === report.viewer_id ? t("teamPage.youName", { name: member.name }) : member.name);
</script>
<style scoped>
@reference "../../../css/tailwind.css";
@layer components {
    /* Fills the page column like the People table; only below ~40rem does the wrapper scroll instead of squeezing Name. */
    .team-members {
        @apply w-full min-w-160 table-fixed border-collapse text-left;
    }
    .team-members th {
        @apply scaffold-details-small text-ink-muted px-4 py-3 whitespace-nowrap;
    }
    .team-members td {
        @apply border-t border-border px-4 py-4;
    }
    .team-member-link::after {
        content: "";
        @apply absolute inset-0 z-10;
    }
    /* Name (column 1) takes whatever the fixed columns leave. */
    .team-members th:nth-child(2),
    .team-members th:nth-child(3) {
        @apply w-[28%];
    }
    /* The right padding keeps the chip and menu clear of the chevron. Scoped under .team-members so it outranks the shared td padding. */
    .team-members .team-member-actions {
        @apply pr-10 pl-0 text-right;
    }
}
</style>
